// src/pages/pengamatan/buah-alami/detail-tabs/PopulasiPerPlotTab.jsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import PopulasiPerPlotTableHeader from './PopulasiPerPlotTableHeader';

const PopulasiPerPlotTab = ({ pengamatanData, filteredPlots }) => {
    // FUNGSI PERBAIKAN: Format angka dengan presisi tinggi
    const formatAngkaExcel = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Jika hasilnya 0.000 atau -0.000, tampilkan "-"
        if (Math.abs(num) < 0.0001) {
            return '-';
        }
        
        // GUNAKAN PRESISI LEBIH TINGGI UNTUK PERHITUNGAN
        const rounded = Math.round(num * 1000) / 1000; // Round to 3 decimal places
        
        // Jika angka >= 1, tampilkan 3 angka di belakang koma
        if (rounded >= 1) {
            const formatted = rounded.toFixed(3);
            const parts = formatted.split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1] || '';
            
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            
            return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
        } 
        // Jika angka < 1, tampilkan hanya bagian desimal (tanpa "0.")
        else {
            const decimalPart = rounded.toFixed(3).split('.')[1];
            
            if (decimalPart === '000') {
                return '-';
            }
            
            return decimalPart;
        }
    };

    // FUNGSI PERBAIKAN: Format khusus untuk baris total (tanpa leading zeros)
    const formatAngkaTotal = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Jika hasilnya 0.000 atau -0.000, tampilkan "-"
        if (Math.abs(num) < 0.0001) {
            return '-';
        }
        
        // GUNAKAN PRESISI LEBIH TINGGI UNTUK PERHITUNGAN
        const rounded = Math.round(num * 1000) / 1000;
        
        // Jika angka >= 1, tampilkan 3 angka di belakang koma
        if (rounded >= 1) {
            const formatted = rounded.toFixed(3);
            const parts = formatted.split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1] || '';
            
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            
            return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
        } 
        // Jika angka < 1, tampilkan hanya bagian desimal (tanpa "0.") dan HAPUS LEADING ZEROS
        else {
            const decimalPart = rounded.toFixed(3).split('.')[1];
            
            if (decimalPart === '000') {
                return '-';
            }
            
            // HAPUS SEMUA LEADING ZEROS
            return decimalPart.replace(/^0+/, '');
        }
    };

    // FUNGSI BARU: Format Luas Plot (2 digit di belakang titik)
    const formatLuasPlot = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Format dengan 2 digit di belakang koma
        const formatted = num.toFixed(2);
        const parts = formatted.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1] || '';
        
        // Format bagian integer dengan pemisah ribuan (titik)
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        
        return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    };

    // FUNGSI BARU: Format PJP (hilangkan angka di belakang titik)
    const formatPJP = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Hilangkan angka di belakang titik (format sebagai integer)
        const integerPart = Math.round(num).toString();
        
        // Format dengan pemisah ribuan (titik)
        return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // FUNGSI PERBAIKAN: Hitung populasi per Ha dengan presisi tinggi
    const calculatePopulationPerHa = () => {
        const plotsToUse = filteredPlots && filteredPlots.length > 0 
            ? filteredPlots 
            : (pengamatanData.plot ? Object.entries(pengamatanData.plot).map(([plotKey, plotData]) => ({
                plotKey,
                ...plotData
            })) : []);
            
        return plotsToUse
            .map((plot) => {
                const dataEntry = plot.data_entry || {};
                
                const totalBA = parseFloat(dataEntry.total_ba) || 0;
                const pjp = parseFloat(plot.pjp) || 1;
                const luasPlot = parseFloat(plot.luas_plot) || 1;
                
                // PERBAIKAN: Gunakan presisi tinggi dalam perhitungan
                const calculateValue = (percentage) => {
                    // Gunakan urutan operasi yang sama dengan Excel
                    const result = (totalBA / pjp) * 16666.67 * (percentage / 100);
                    // Return nilai asli tanpa pembulatan
                    return result / 1000;
                };

                // FUNGSI PERBAIKAN: Hitung prosentase fase bunga dengan presisi
                const calculateFaseBungaPercentage = (plotData, fieldName) => {
                    const faseBunga = plotData.data_entry?.fase_bunga || {};
                    const total = parseFloat(plotData.data_entry?.total_ba) || 0;
                    const fieldValue = parseFloat(faseBunga[fieldName]) || 0;
                        
                    if (total > 0) {
                        // PERBAIKAN: Gunakan pembulatan yang konsisten
                        return (fieldValue / total) * 100;
                    }
                    return 0;
                };

                // FUNGSI PERBAIKAN: Hitung prosentase tanaman dengan presisi
                const calculateTanamanPercentage = (plotData, fieldName) => {
                    const tanaman = plotData.data_entry?.tanaman || {};
                    const penyakit = plotData.data_entry?.tanaman?.penyakit_tanaman || {};
                    const total = parseFloat(plotData.data_entry?.total_ba) || 0;
                    
                    let fieldValue = 0;
                    
                    if (fieldName.includes('penyakit_')) {
                        const penyakitField = fieldName.replace('penyakit_', '');
                        fieldValue = parseFloat(penyakit[`${penyakitField}_pt_ba`]) || 0;
                    } else {
                        fieldValue = parseFloat(tanaman[`${fieldName}_t_ba`]) || 0;
                    }
                    
                    if (total > 0) {
                        return (fieldValue / total) * 100;
                    }
                    return 0;
                };

                // FUNGSI PERBAIKAN: Hitung prosentase buah alami dan mati dengan presisi
                const calculateBuahAlamiMatiPercentage = (plotData, fieldName) => {
                    const dataEntry = plotData.data_entry || {};
                    const total = parseFloat(dataEntry.total_ba) || 0;
                    
                    let fieldValue = 0;
                    
                    if (fieldName === 'buah_alami_ba') {
                        fieldValue = parseFloat(dataEntry.buah_alami_ba) || 0;
                    } else if (fieldName === 'mati_ba') {
                        fieldValue = parseFloat(dataEntry.mati_ba) || 0;
                    }
                    
                    if (total > 0) {
                        return (fieldValue / total) * 100;
                    }
                    return 0;
                };

                // Ambil presentase dari setiap fase bunga
                const openHeartPercentage = calculateFaseBungaPercentage(plot, 'open_heart_fb_ba');
                const redBud125Percentage = calculateFaseBungaPercentage(plot, 'red_bud_125_fb_ba');
                const redBud254Percentage = calculateFaseBungaPercentage(plot, 'red_bud_254_fb_ba');
                const earlyConesPercentage = calculateFaseBungaPercentage(plot, 'early_cones_fb_ba');
                const midConesPercentage = calculateFaseBungaPercentage(plot, 'mid_cones_fb_ba');
                const lateConesPercentage = calculateFaseBungaPercentage(plot, 'late_cones_fb_ba');
                const earlyPetalsPercentage = calculateFaseBungaPercentage(plot, 'early_petals_fb_ba');
                const midPetalsPercentage = calculateFaseBungaPercentage(plot, 'mid_petals_fb_ba');
                const latePetalsPercentage = calculateFaseBungaPercentage(plot, 'late_petals_fb_ba');
                const dryPetalsPercentage = calculateFaseBungaPercentage(plot, 'dry_petals_fb_ba');
                const fillingStage1Percentage = calculateFaseBungaPercentage(plot, 'filling_stage_1_fb_ba');
                const fillingStage2Percentage = calculateFaseBungaPercentage(plot, 'filling_stage_2_fb_ba');
                const sc0Percentage = calculateFaseBungaPercentage(plot, 'sc0_fb_ba');
                const sc0LebihPercentage = calculateFaseBungaPercentage(plot, 'sc0_ld_fb_ba');
                const terpetikPercentage = calculateFaseBungaPercentage(plot, 'terpetik_fb_ba');
                const dimakanTikusPercentage = calculateFaseBungaPercentage(plot, 'dimakan_tikus_fb_ba');

                // Ambil presentase dari tanaman
                const normalTanamanPercentage = calculateTanamanPercentage(plot, 'normal');
                const penyakitPhyPercentage = calculateTanamanPercentage(plot, 'penyakit_phy');
                const penyakitErwPercentage = calculateTanamanPercentage(plot, 'penyakit_erw');
                const kerdilPercentage = calculateTanamanPercentage(plot, 'kerdil');

                // Ambil presentase untuk buah alami dan mati
                const buahAlamiPercentage = calculateBuahAlamiMatiPercentage(plot, 'buah_alami_ba');
                const matiPercentage = calculateBuahAlamiMatiPercentage(plot, 'mati_ba');

                // PERBAIKAN: Hitung nilai dengan presisi tinggi
                const openHeart = calculateValue(openHeartPercentage);
                const redBud125 = calculateValue(redBud125Percentage);
                const redBud254 = calculateValue(redBud254Percentage);
                const earlyCones = calculateValue(earlyConesPercentage);
                const midCones = calculateValue(midConesPercentage);
                const lateCones = calculateValue(lateConesPercentage);
                const earlyPetals = calculateValue(earlyPetalsPercentage);
                const midPetals = calculateValue(midPetalsPercentage);
                const latePetals = calculateValue(latePetalsPercentage);
                const dryPetals = calculateValue(dryPetalsPercentage);
                const fillingStage1 = calculateValue(fillingStage1Percentage);
                const fillingStage2 = calculateValue(fillingStage2Percentage);
                const sc0 = calculateValue(sc0Percentage);
                const sc0Lebih = calculateValue(sc0LebihPercentage);
                const terpetik = calculateValue(terpetikPercentage);
                const dimakanTikus = calculateValue(dimakanTikusPercentage);
                const normalTanaman = calculateValue(normalTanamanPercentage);
                const penyakitPhy = calculateValue(penyakitPhyPercentage);
                const penyakitErw = calculateValue(penyakitErwPercentage);
                const kerdil = calculateValue(kerdilPercentage);
                const buahAlami = calculateValue(buahAlamiPercentage);
                const mati = calculateValue(matiPercentage);

                return {
                    plot: plot.plot || plot.plotKey,
                    luasPlot: plot.luas_plot || '-',
                    pjp: plot.pjp || '-',
                    dataEntry: dataEntry,
                    
                    // Data fase bunga dari Populasi Per Ha (NILAI ASLI TANPA PEMBULATAN)
                    openHeartPerHa: openHeart,
                    redBud125PerHa: redBud125,
                    redBud254PerHa: redBud254,
                    earlyConesPerHa: earlyCones,
                    midConesPerHa: midCones,
                    lateConesPerHa: lateCones,
                    earlyPetalsPerHa: earlyPetals,
                    midPetalsPerHa: midPetals,
                    latePetalsPerHa: latePetals,
                    dryPetalsPerHa: dryPetals,
                    fillingStage1PerHa: fillingStage1,
                    fillingStage2PerHa: fillingStage2,
                    sc0PerHa: sc0,
                    sc0LebihPerHa: sc0Lebih,
                    terpetikPerHa: terpetik,
                    dimakanTikusPerHa: dimakanTikus,
                    
                    // Data tanaman dari Populasi Per Ha (NILAI ASLI TANPA PEMBULATAN)
                    normalTanamanPerHa: normalTanaman,
                    penyakitPhyPerHa: penyakitPhy,
                    penyakitErwPerHa: penyakitErw,
                    kerdilPerHa: kerdil,
                    
                    // Data buah alami dan mati dari Populasi Per Ha (NILAI ASLI TANPA PEMBULATAN)
                    buahAlamiPerHa: buahAlami,
                    matiPerHa: mati
                };
            })
            .sort((a, b) => {
                if (!isNaN(a.plot) && !isNaN(b.plot)) {
                    return parseInt(a.plot) - parseInt(b.plot);
                }
                return a.plot.localeCompare(b.plot);
            });
    };

    // FUNGSI PERBAIKAN: Hitung populasi per plot dengan presisi tinggi
    const getPopulationPerPlot = () => {
        const populationPerHaData = calculatePopulationPerHa();
        
        return populationPerHaData.map((item) => {
            const luasPlot = parseFloat(item.luasPlot) || 1;
            
            // PERBAIKAN: Hitung tanpa pembulatan intermediate, baru format di akhir
            const calculatePerPlotValue = (perHaValue) => {
                const result = perHaValue * luasPlot;
                // Round to 3 decimal places untuk menghindari floating point errors
                return Math.round(result * 1000) / 1000;
            };

            return {
                plot: item.plot,
                luasPlot: formatLuasPlot(item.luasPlot), // GUNAKAN FORMAT BARU: 2 digit
                pjp: formatPJP(item.pjp), // GUNAKAN FORMAT BARU: tanpa digit
                dataEntry: item.dataEntry,
                
                // Fase Bunga: Data Per Ha × Luas (diformat di akhir)
                openHeart: formatAngkaExcel(calculatePerPlotValue(item.openHeartPerHa)),
                redBud125: formatAngkaExcel(calculatePerPlotValue(item.redBud125PerHa)),
                redBud254: formatAngkaExcel(calculatePerPlotValue(item.redBud254PerHa)),
                earlyCones: formatAngkaExcel(calculatePerPlotValue(item.earlyConesPerHa)),
                midCones: formatAngkaExcel(calculatePerPlotValue(item.midConesPerHa)),
                lateCones: formatAngkaExcel(calculatePerPlotValue(item.lateConesPerHa)),
                earlyPetals: formatAngkaExcel(calculatePerPlotValue(item.earlyPetalsPerHa)),
                midPetals: formatAngkaExcel(calculatePerPlotValue(item.midPetalsPerHa)),
                latePetals: formatAngkaExcel(calculatePerPlotValue(item.latePetalsPerHa)),
                dryPetals: formatAngkaExcel(calculatePerPlotValue(item.dryPetalsPerHa)),
                fillingStage1: formatAngkaExcel(calculatePerPlotValue(item.fillingStage1PerHa)),
                fillingStage2: formatAngkaExcel(calculatePerPlotValue(item.fillingStage2PerHa)),
                sc0: formatAngkaExcel(calculatePerPlotValue(item.sc0PerHa)),
                sc0Lebih: formatAngkaExcel(calculatePerPlotValue(item.sc0LebihPerHa)),
                terpetik: formatAngkaExcel(calculatePerPlotValue(item.terpetikPerHa)),
                dimakanTikus: formatAngkaExcel(calculatePerPlotValue(item.dimakanTikusPerHa)),
                
                // Tanaman: Data Per Ha × Luas (diformat di akhir)
                normalTanaman: formatAngkaExcel(calculatePerPlotValue(item.normalTanamanPerHa)),
                penyakitPhy: formatAngkaExcel(calculatePerPlotValue(item.penyakitPhyPerHa)),
                penyakitErw: formatAngkaExcel(calculatePerPlotValue(item.penyakitErwPerHa)),
                kerdil: formatAngkaExcel(calculatePerPlotValue(item.kerdilPerHa)),
                
                // Buah Alami & Mati: Data Per Ha × Luas (diformat di akhir)
                buahAlami: formatAngkaExcel(calculatePerPlotValue(item.buahAlamiPerHa)),
                mati: formatAngkaExcel(calculatePerPlotValue(item.matiPerHa))
            };
        });
    };

    // FUNGSI PERBAIKAN: Menghitung total dari semua plot dengan presisi tinggi
    const calculateTotalRow = (populationData) => {
        if (populationData.length === 0) return null;

        // Kumpulkan data total dari semua plot
        let totalBA = 0;
        let totalPJP = 0;
        let totalLuasPlot = 0;

        // Data untuk menghitung presentase total
        let totalFaseBunga = {
            open_heart_fb_ba: 0,
            red_bud_125_fb_ba: 0,
            red_bud_254_fb_ba: 0,
            early_cones_fb_ba: 0,
            mid_cones_fb_ba: 0,
            late_cones_fb_ba: 0,
            early_petals_fb_ba: 0,
            mid_petals_fb_ba: 0,
            late_petals_fb_ba: 0,
            dry_petals_fb_ba: 0,
            filling_stage_1_fb_ba: 0,
            filling_stage_2_fb_ba: 0,
            sc0_fb_ba: 0,
            sc0_ld_fb_ba: 0,
            terpetik_fb_ba: 0,
            dimakan_tikus_fb_ba: 0
        };

        let totalTanaman = {
            normal_t_ba: 0,
            kerdil_t_ba: 0
        };

        let totalPenyakit = {
            phy_pt_ba: 0,
            erw_pt_ba: 0
        };

        let totalBuahAlami = 0;
        let totalMati = 0;
        let overallTotalBA = 0;

        // Kumpulkan data dari semua plot
        populationData.forEach(item => {
            const dataEntry = item.dataEntry || {};
            const faseBunga = dataEntry.fase_bunga || {};
            const tanaman = dataEntry.tanaman || {};
            const penyakit = dataEntry.tanaman?.penyakit_tanaman || {};
            
            const plotTotalBA = parseFloat(dataEntry.total_ba) || 0;
            const plotPJP = parseFloat(item.pjp) || 0;
            const plotLuasPlot = parseFloat(item.luasPlot) || 0;

            totalBA += plotTotalBA;
            totalPJP += plotPJP;
            totalLuasPlot += plotLuasPlot;
            overallTotalBA += plotTotalBA;

            // Kumpulkan data fase bunga
            Object.keys(totalFaseBunga).forEach(key => {
                totalFaseBunga[key] += parseFloat(faseBunga[key]) || 0;
            });

            // Kumpulkan data tanaman
            totalTanaman.normal_t_ba += parseFloat(tanaman.normal_t_ba) || 0;
            totalTanaman.kerdil_t_ba += parseFloat(tanaman.kerdil_t_ba) || 0;

            // Kumpulkan data penyakit
            totalPenyakit.phy_pt_ba += parseFloat(penyakit.phy_pt_ba) || 0;
            totalPenyakit.erw_pt_ba += parseFloat(penyakit.erw_pt_ba) || 0;

            // Kumpulkan data buah alami dan mati
            totalBuahAlami += parseFloat(dataEntry.buah_alami_ba) || 0;
            totalMati += parseFloat(dataEntry.mati_ba) || 0;
        });

        // PERBAIKAN: Hitung presentase total dengan presisi tinggi
        const calculateTotalPercentage = (fieldValue) => {
            if (overallTotalBA > 0) {
                return (fieldValue / overallTotalBA) * 100;
            }
            return 0;
        };

        // PERBAIKAN: Hitung nilai total dengan presisi tinggi
        const calculateTotalValue = (percentage) => {
            if (totalPJP > 0) {
                const result = (totalBA / totalPJP) * 16666.67 * (percentage / 100);
                // Return nilai asli tanpa pembulatan
                return result / 1000;
            }
            return 0;
        };

        // Hitung presentase total untuk setiap fase bunga
        const openHeartPercentage = calculateTotalPercentage(totalFaseBunga.open_heart_fb_ba);
        const redBud125Percentage = calculateTotalPercentage(totalFaseBunga.red_bud_125_fb_ba);
        const redBud254Percentage = calculateTotalPercentage(totalFaseBunga.red_bud_254_fb_ba);
        const earlyConesPercentage = calculateTotalPercentage(totalFaseBunga.early_cones_fb_ba);
        const midConesPercentage = calculateTotalPercentage(totalFaseBunga.mid_cones_fb_ba);
        const lateConesPercentage = calculateTotalPercentage(totalFaseBunga.late_cones_fb_ba);
        const earlyPetalsPercentage = calculateTotalPercentage(totalFaseBunga.early_petals_fb_ba);
        const midPetalsPercentage = calculateTotalPercentage(totalFaseBunga.mid_petals_fb_ba);
        const latePetalsPercentage = calculateTotalPercentage(totalFaseBunga.late_petals_fb_ba);
        const dryPetalsPercentage = calculateTotalPercentage(totalFaseBunga.dry_petals_fb_ba);
        const fillingStage1Percentage = calculateTotalPercentage(totalFaseBunga.filling_stage_1_fb_ba);
        const fillingStage2Percentage = calculateTotalPercentage(totalFaseBunga.filling_stage_2_fb_ba);
        const sc0Percentage = calculateTotalPercentage(totalFaseBunga.sc0_fb_ba);
        const sc0LebihPercentage = calculateTotalPercentage(totalFaseBunga.sc0_ld_fb_ba);
        const terpetikPercentage = calculateTotalPercentage(totalFaseBunga.terpetik_fb_ba);
        const dimakanTikusPercentage = calculateTotalPercentage(totalFaseBunga.dimakan_tikus_fb_ba);

        // Hitung presentase total untuk tanaman
        const normalTanamanPercentage = calculateTotalPercentage(totalTanaman.normal_t_ba);
        const penyakitPhyPercentage = calculateTotalPercentage(totalPenyakit.phy_pt_ba);
        const penyakitErwPercentage = calculateTotalPercentage(totalPenyakit.erw_pt_ba);
        const kerdilPercentage = calculateTotalPercentage(totalTanaman.kerdil_t_ba);

        // Hitung presentase total untuk buah alami dan mati
        const buahAlamiPercentage = calculateTotalPercentage(totalBuahAlami);
        const matiPercentage = calculateTotalPercentage(totalMati);

        // PERBAIKAN: Hitung nilai total Per Ha dengan presisi tinggi
        const openHeartPerHa = calculateTotalValue(openHeartPercentage);
        const redBud125PerHa = calculateTotalValue(redBud125Percentage);
        const redBud254PerHa = calculateTotalValue(redBud254Percentage);
        const earlyConesPerHa = calculateTotalValue(earlyConesPercentage);
        const midConesPerHa = calculateTotalValue(midConesPercentage);
        const lateConesPerHa = calculateTotalValue(lateConesPercentage);
        const earlyPetalsPerHa = calculateTotalValue(earlyPetalsPercentage);
        const midPetalsPerHa = calculateTotalValue(midPetalsPercentage);
        const latePetalsPerHa = calculateTotalValue(latePetalsPercentage);
        const dryPetalsPerHa = calculateTotalValue(dryPetalsPercentage);
        const fillingStage1PerHa = calculateTotalValue(fillingStage1Percentage);
        const fillingStage2PerHa = calculateTotalValue(fillingStage2Percentage);
        const sc0PerHa = calculateTotalValue(sc0Percentage);
        const sc0LebihPerHa = calculateTotalValue(sc0LebihPercentage);
        const terpetikPerHa = calculateTotalValue(terpetikPercentage);
        const dimakanTikusPerHa = calculateTotalValue(dimakanTikusPercentage);
        const normalTanamanPerHa = calculateTotalValue(normalTanamanPercentage);
        const penyakitPhyPerHa = calculateTotalValue(penyakitPhyPercentage);
        const penyakitErwPerHa = calculateTotalValue(penyakitErwPercentage);
        const kerdilPerHa = calculateTotalValue(kerdilPercentage);
        const buahAlamiPerHa = calculateTotalValue(buahAlamiPercentage);
        const matiPerHa = calculateTotalValue(matiPercentage);

        // PERBAIKAN: KONVERSI KE PER PLOT dengan presisi tinggi
        const calculateTotalPerPlot = (perHaValue) => {
            const result = perHaValue * totalLuasPlot;
            return Math.round(result * 1000) / 1000;
        };

        return {
            plot: 'Total',
            luasPlot: formatLuasPlot(totalLuasPlot), // GUNAKAN FORMAT BARU: 2 digit
            pjp: formatPJP(totalPJP), // GUNAKAN FORMAT BARU: tanpa digit
            
            // Data fase bunga - GUNAKAN formatAngkaTotal untuk menghapus leading zeros
            openHeart: formatAngkaTotal(calculateTotalPerPlot(openHeartPerHa)),
            redBud125: formatAngkaTotal(calculateTotalPerPlot(redBud125PerHa)),
            redBud254: formatAngkaTotal(calculateTotalPerPlot(redBud254PerHa)),
            earlyCones: formatAngkaTotal(calculateTotalPerPlot(earlyConesPerHa)),
            midCones: formatAngkaTotal(calculateTotalPerPlot(midConesPerHa)),
            lateCones: formatAngkaTotal(calculateTotalPerPlot(lateConesPerHa)),
            earlyPetals: formatAngkaTotal(calculateTotalPerPlot(earlyPetalsPerHa)),
            midPetals: formatAngkaTotal(calculateTotalPerPlot(midPetalsPerHa)),
            latePetals: formatAngkaTotal(calculateTotalPerPlot(latePetalsPerHa)),
            dryPetals: formatAngkaTotal(calculateTotalPerPlot(dryPetalsPerHa)),
            fillingStage1: formatAngkaTotal(calculateTotalPerPlot(fillingStage1PerHa)),
            fillingStage2: formatAngkaTotal(calculateTotalPerPlot(fillingStage2PerHa)),
            sc0: formatAngkaTotal(calculateTotalPerPlot(sc0PerHa)),
            sc0Lebih: formatAngkaTotal(calculateTotalPerPlot(sc0LebihPerHa)),
            terpetik: formatAngkaTotal(calculateTotalPerPlot(terpetikPerHa)),
            dimakanTikus: formatAngkaTotal(calculateTotalPerPlot(dimakanTikusPerHa)),
            
            // Data tanaman - GUNAKAN formatAngkaTotal untuk menghapus leading zeros
            normalTanaman: formatAngkaTotal(calculateTotalPerPlot(normalTanamanPerHa)),
            penyakitPhy: formatAngkaTotal(calculateTotalPerPlot(penyakitPhyPerHa)),
            penyakitErw: formatAngkaTotal(calculateTotalPerPlot(penyakitErwPerHa)),
            kerdil: formatAngkaTotal(calculateTotalPerPlot(kerdilPerHa)),
            
            // Data buah alami dan mati - GUNAKAN formatAngkaTotal untuk menghapus leading zeros
            buahAlami: formatAngkaTotal(calculateTotalPerPlot(buahAlamiPerHa)),
            mati: formatAngkaTotal(calculateTotalPerPlot(matiPerHa))
        };
    };

    const populationData = getPopulationPerPlot();
    const totalData = calculateTotalRow(populationData);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Data Populasi Per Plot
            </Typography>
            
            <Card>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <PopulasiPerPlotTableHeader/>
                        <TableBody>
                            {populationData.map((item, index) => (
                                <TableRow key={index}>
                                    {/* Plot */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {item.plot}
                                    </TableCell>
                                    
                                    {/* Luas - GUNAKAN FORMAT BARU: 2 digit */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {item.luasPlot}
                                    </TableCell>
                                    
                                    {/* PJP - GUNAKAN FORMAT BARU: tanpa digit */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {item.pjp}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Data */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.midCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.sc0}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.sc0Lebih}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.terpetik}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.dimakanTikus}
                                    </TableCell>
                                    
                                    {/* Tanaman - Data */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.normalTanaman}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.penyakitPhy}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.penyakitErw}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.kerdil}
                                    </TableCell>

                                    {/* 2 KOLOM TERAKHIR: Buah Alami dan Mati */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.buahAlami}
                                    </TableCell>
                                    <TableCell align="right">
                                        {item.mati}
                                    </TableCell>
                                </TableRow>
                            ))}
                            
                            {/* ROW TOTAL */}
                            {totalData && (
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.plot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.luasPlot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.pjp}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.midCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.sc0}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.sc0Lebih}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.terpetik}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.dimakanTikus}
                                    </TableCell>
                                    
                                    {/* Tanaman - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.normalTanaman}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.penyakitPhy}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.penyakitErw}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.kerdil}
                                    </TableCell>

                                    {/* 2 KOLOM TERAKHIR: Buah Alami dan Mati - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.buahAlami}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                        {totalData.mati}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {populationData.length === 0 && (
                    <Typography color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                        Tidak ada data populasi per plot
                    </Typography>
                )}
            </Card>
        </Box>
    );
};

export default PopulasiPerPlotTab;